import Link from '@alfa-retail-react/components/link';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import { useController } from 'react-hook-form';
import RadioGroup from '@alfa-retail-react/components/radio-group';
import Radio from '@alfa-retail-react/components/radio';
import { observer } from 'mobx-react';
import { useFormContext } from '@alfa-retail-react/components/form-hook';

import { useIsDesktop } from 'app/utils/useWindowType';
import { FormatUtils } from 'app/utils/FormatUtils';
import { ResourceFormat } from 'app/model/ResourceFormat';
import {
    OpenResourceMode,
    DownloadResourceLink,
} from 'app/component/DownloadResourceLink';
import { InsuranceProduct } from 'app/model/product/InsuranceProduct';
import { ValidationRules } from 'app/form/ValidationRules';
import { FileInformation } from 'app/utils/FilesUtils';
import { ProductParametersConfigureFormStore } from 'app/logic/pages/productParametersConfigure/ProductParametersConfigureFormStore';
import { ProductParametersConfigureFormValues } from 'app/logic/pages/productParametersConfigure/ProductParametersConfigurePage';
import { useI18nContext } from 'app/store/сontext/I18nContext';

const InsuranceRadioButtonLabel = ({
    insurance,
}: {
    insurance: InsuranceProduct;
}) => {
    return (
        <span className={'insuranceRadioButtonLabel'}>
            <span className={'amount'}>
                {FormatUtils.toIntegerString(insurance.amount)}
            </span>{' '}
            <span className={'currency'}>{insurance.currency}</span>
            {' - '}
            <span className={'name'}>{insurance.name}</span>
        </span>
    );
};

export interface InsuranceChoiceFormItemProps {
    formStore: ProductParametersConfigureFormStore;

    downloadInsuranceInstructions: (
        productCode: string,
        documentCode: string,
        format: ResourceFormat,
    ) => Promise<FileInformation>;
}

const getDescriptionKey = (code: string, isDesktop: boolean) => `insurance_code_${code}_description${isDesktop ? '' : '_short'}`;

export const InsuranceChoiceFormItem = observer(
    ({
        formStore,
        downloadInsuranceInstructions,
    }: InsuranceChoiceFormItemProps) => {
        const INSURANCE_CHOICE_FORM_ITEM_NAME = 'insurance';
        const [isDesktop] = useIsDesktop();
        const { t } = useI18nContext();

        const moreLabel = isDesktop
            ? t('redCard_terms_button_insuranceTerms')
            : 'Подробные условия';

        const { control, setValue, handleMultipleErrors, setFieldElement } =
            useFormContext<ProductParametersConfigureFormValues>();

        const { field, fieldState } =
            useController<ProductParametersConfigureFormValues>({
                name: INSURANCE_CHOICE_FORM_ITEM_NAME,
                control,
                defaultValue: formStore.insuranceProduct?.code,
                rules: { required: ValidationRules.required() },
            });

        const handleChangeChoice = (value: string) => {
            const insurance =
                formStore.insuranceStore.getInsuranceByCode(value);
            formStore.setInsurance(insurance);
            setValue(INSURANCE_CHOICE_FORM_ITEM_NAME, insurance.code);
        };

        useEffect(() => {
            if (
                !formStore.availableInsuranceList.find(
                    (insurance) =>
                        insurance.code === formStore.insuranceProduct.code,
                )
            ) {
                handleChangeChoice(formStore.defaultInsurance.code);
            }
        }, [formStore.creditLimit]);

        return (
            <RadioGroup
                ref={(element) => {
                    field.ref(element);
                    setFieldElement(INSURANCE_CHOICE_FORM_ITEM_NAME, element);
                }}
                direction={'vertical'}
                value={String(field.value)}
                name={'radioGroup'}
                error={handleMultipleErrors(fieldState.error)}
                onBlur={field.onBlur}
                disabled={!formStore.hasAvailableInsurance}
                onChange={(event, payload) => {
                    handleChangeChoice(payload.value);
                }}
            >
                {formStore.displayInsuranceList.map((insurance, index) => {
                    const descriptionTranslateCode = getDescriptionKey(insurance.code, isDesktop);

                    return (
                        <Radio
                            className={'insuranceRadioOld'}
                            size="m"
                            key={index}
                            value={insurance.code}
                            label={
                                <InsuranceRadioButtonLabel
                                    insurance={insurance}
                                />
                            }
                            block
                            hint={
                                <>
                                    <div className={'insuranceChoiceHint'}>
                                        {t(descriptionTranslateCode)}
                                    </div>
                                    <DownloadResourceLink
                                        className={classNames(
                                            'insuranceResourceLink',
                                            { short: !isDesktop },
                                        )}
                                        openResourceMode={
                                            OpenResourceMode.NEW_TAB
                                        }
                                        trigger={
                                            <Link
                                                view="default"
                                                underline={isDesktop}
                                                href={'#'}
                                                onClick={(event) =>
                                                    event.preventDefault()
                                                }
                                            >
                                                {moreLabel}
                                            </Link>
                                        }
                                        title={moreLabel}
                                        download={() =>
                                            downloadInsuranceInstructions(
                                                insurance.code,
                                                insurance.conditionsDocumentCode,
                                                ResourceFormat.PDF,
                                            )
                                        }
                                    />
                                </>
                            }
                        />
                    );
                })}
            </RadioGroup>
        );
    },
);
